import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Calendar, Clock, MessageSquare, Users, BookOpen, ArrowRight, Sparkles, Ticket, MapPin } from 'lucide-react';
import { formatDate as formatUtcDate, formatTime as formatUtcTime } from '../utils/datetime';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
    const { user, refreshUser } = useAuth();
    const [searchParams] = useSearchParams();
    const [recentPosts, setRecentPosts] = useState([]);
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [myBookings, setMyBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check for payment success
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
            pollPaymentStatus(sessionId);
        }
        
        fetchDashboardData();
    }, [searchParams]);

    const pollPaymentStatus = async (sessionId, attempts = 0) => {
        const maxAttempts = 5;
        const pollInterval = 2000;

        if (attempts >= maxAttempts) {
            toast.info('Payment status is being processed. Please check your email for confirmation.');
            return;
        }

        try {
            const { data } = await axios.get(`${API_URL}/api/membership/checkout/status/${sessionId}`, { withCredentials: true });
            
            if (data.payment_status === 'paid') {
                toast.success('Payment successful! Welcome to the Mirror Circle!');
                await refreshUser();
                // Remove session_id from URL
                window.history.replaceState({}, document.title, '/dashboard');
                return;
            } else if (data.status === 'expired') {
                toast.error('Payment session expired. Please try again.');
                return;
            }

            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
        } catch (error) {
            console.error('Error checking payment status:', error);
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
        }
    };

    const fetchDashboardData = async () => {
        try {
            // Fetch recent forum posts if member
            if (user?.membership_tier) {
                try {
                    const postsRes = await axios.get(`${API_URL}/api/forum/posts?limit=3`, { withCredentials: true });
                    setRecentPosts(postsRes.data);
                } catch (e) {
                    console.log('Forum not accessible');
                }
            }

            // Fetch upcoming events
            try {
                const eventsRes = await axios.get(`${API_URL}/api/events?upcoming_only=true`, { withCredentials: true });
                setUpcomingEvents(eventsRes.data.slice(0, 3));
            } catch (e) {
                console.log('Events not accessible');
            }

            // Fetch my event bookings
            try {
                const bookingsRes = await axios.get(`${API_URL}/api/events/my/bookings`, { withCredentials: true });
                setMyBookings(bookingsRes.data);
            } catch (e) {
                console.log('Bookings not accessible');
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getMembershipBadgeClass = (tier) => {
        switch (tier) {
            case 'seeker': return 'tier-badge tier-seeker';
            case 'walker': return 'tier-badge tier-walker';
            case 'architect': return 'tier-badge tier-architect';
            case 'partnership': return 'tier-badge tier-partnership';
            default: return 'tier-badge bg-gray-100 text-gray-600';
        }
    };

    const formatDate = (dateStr) => formatUtcDate(dateStr);

    const getDaysRemaining = () => {
        if (!user?.membership_expires) return null;
        const expires = new Date(user.membership_expires);
        const now = new Date();
        const days = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
        return days;
    };

    const daysRemaining = getDaysRemaining();

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />

            <main className="pt-24 pb-16" data-testid="dashboard-page">
                <div className="max-w-7xl mx-auto px-6 sm:px-12">
                    {/* Welcome Section */}
                    <div className="mb-12">
                        <p className="overline mb-2">Welcome back</p>
                        <h1 className="heading-2 mb-4" data-testid="dashboard-welcome">
                            {user?.name || 'Member'}
                        </h1>
                        <p className="body-text">Your journey continues here.</p>
                    </div>

                    {/* Membership Status */}
                    <div className="content-card mb-8" data-testid="membership-status">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                                <p className="font-sans text-sm text-[#4A4A68] mb-2">Membership Status</p>
                                {user?.membership_tier ? (
                                    <div className="flex items-center gap-3">
                                        <span className={getMembershipBadgeClass(user.membership_tier)} data-testid="membership-tier-badge">
                                            {user.membership_tier}
                                        </span>
                                        {daysRemaining !== null && (
                                            <span className="font-sans text-sm text-[#4A4A68]">
                                                {daysRemaining > 0 ? (
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-4 h-4" />
                                                        {daysRemaining} days remaining
                                                    </span>
                                                ) : (
                                                    <span className="text-red-500">Membership expired</span>
                                                )}
                                            </span>
                                        )}
                                    </div>
                                ) : (
                                    <p className="font-sans text-[#1A1A2E]">No active membership</p>
                                )}
                            </div>
                            <Link 
                                to="/membership" 
                                className="btn-secondary text-xs"
                                data-testid="extend-membership-btn"
                            >
                                {user?.membership_tier ? 'Extend Membership' : 'Choose Membership'}
                            </Link>
                        </div>
                    </div>

                    {/* Quick Access Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                        <Link to="/forum" className="content-card group" data-testid="quick-access-forum">
                            <MessageSquare className="w-8 h-8 text-[#5B2D8E] mb-4 group-hover:scale-110 transition-transform" />
                            <h3 className="font-serif text-xl text-[#1A1A2E] mb-2">Forum</h3>
                            <p className="font-sans text-sm text-[#4A4A68]">Join discussions with fellow seekers</p>
                        </Link>
                        
                        <Link to="/events" className="content-card group" data-testid="quick-access-events">
                            <Calendar className="w-8 h-8 text-[#5B2D8E] mb-4 group-hover:scale-110 transition-transform" />
                            <h3 className="font-serif text-xl text-[#1A1A2E] mb-2">Events</h3>
                            <p className="font-sans text-sm text-[#4A4A68]">Discover upcoming gatherings</p>
                        </Link>
                        
                        <Link to="/library" className="content-card group" data-testid="quick-access-library">
                            <BookOpen className="w-8 h-8 text-[#5B2D8E] mb-4 group-hover:scale-110 transition-transform" />
                            <h3 className="font-serif text-xl text-[#1A1A2E] mb-2">Library</h3>
                            <p className="font-sans text-sm text-[#4A4A68]">Access resources and tools</p>
                        </Link>
                        
                        <Link to="/connections" className="content-card group" data-testid="quick-access-connections">
                            <Users className="w-8 h-8 text-[#5B2D8E] mb-4 group-hover:scale-110 transition-transform" />
                            <h3 className="font-serif text-xl text-[#1A1A2E] mb-2">Connections</h3>
                            <p className="font-sans text-sm text-[#4A4A68]">Connect with other members</p>
                        </Link>
                    </div>

                    {/* Recent Activity */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                        {/* Recent Forum Posts */}
                        {user?.membership_tier && (
                            <div data-testid="recent-posts-section">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="heading-3">Recent Discussions</h2>
                                    <Link to="/forum" className="text-[#5B2D8E] text-sm flex items-center gap-1 hover:underline">
                                        View all <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                                {recentPosts.length > 0 ? (
                                    <div className="space-y-4">
                                        {recentPosts.map((post) => (
                                            <Link 
                                                key={post.id} 
                                                to={`/forum/${post.id}`}
                                                className="content-card block"
                                            >
                                                <h4 className="font-serif text-lg text-[#1A1A2E] mb-2">{post.title}</h4>
                                                <div className="flex items-center gap-4 text-sm text-[#4A4A68]">
                                                    <span>{post.author_name}</span>
                                                    <span>•</span>
                                                    <span>{post.reply_count || 0} replies</span>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="content-card text-center py-8">
                                        <Sparkles className="w-8 h-8 text-[#D8B4E2] mx-auto mb-4" />
                                        <p className="text-[#4A4A68]">No discussions yet. Be the first to start one!</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Upcoming Events */}
                        <div data-testid="upcoming-events-section">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="heading-3">Upcoming Events</h2>
                                <Link to="/events" className="text-[#5B2D8E] text-sm flex items-center gap-1 hover:underline">
                                    View all <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                            {upcomingEvents.length > 0 ? (
                                <div className="space-y-4">
                                    {upcomingEvents.map((event) => (
                                        <div key={event.id} className="content-card">
                                            <h4 className="font-serif text-lg text-[#1A1A2E] mb-2">{event.title}</h4>
                                            <div className="flex items-center gap-4 text-sm text-[#4A4A68]">
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-4 h-4" />
                                                    {formatDate(event.date)}
                                                </span>
                                                <span>•</span>
                                                <span>{event.location}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="content-card text-center py-8">
                                    <Calendar className="w-8 h-8 text-[#D8B4E2] mx-auto mb-4" />
                                    <p className="text-[#4A4A68]">No upcoming events at the moment.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* My Booked Events */}
                    {myBookings.length > 0 && (
                        <div data-testid="my-bookings-section" className="mb-12">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="heading-3">
                                    <Ticket className="w-6 h-6 inline mr-2 text-[#5B2D8E]" />
                                    My Booked Events
                                </h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {myBookings.map((booking) => (
                                    <div 
                                        key={booking.id} 
                                        className="content-card border-l-4 border-l-[#5B2D8E]"
                                        data-testid={`booking-${booking.id}`}
                                    >
                                        {booking.event ? (
                                            <>
                                                <h4 className="font-serif text-lg text-[#1A1A2E] mb-3">
                                                    {booking.event.title}
                                                </h4>
                                                <div className="space-y-2 text-sm text-[#4A4A68]">
                                                    <div className="flex items-center gap-2">
                                                        <Calendar className="w-4 h-4 text-[#5B2D8E]" />
                                                        {formatDate(booking.event.date)}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Clock className="w-4 h-4 text-[#5B2D8E]" />
                                                        {formatUtcTime(booking.event.date)}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <MapPin className="w-4 h-4 text-[#5B2D8E]" />
                                                        {booking.event.location}
                                                    </div>
                                                </div>
                                                <div className="mt-4 pt-4 border-t border-[#E5E0D8]">
                                                    <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs rounded-sm">
                                                        Confirmed
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <p className="text-[#4A4A68]">Event details loading...</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}
